/**
 * PDP Size & Fit — switch model measurements when size >= 16.
 */
(function () {
  const PLUS_MIN = 16;
  const ROOT_SELECTOR = "[data-size-and-fit]";

  function parseSizeNumber(value) {
    if (value == null || value === "") return null;
    const match = String(value).trim().match(/(\d+)/);
    return match ? Number(match[1]) : null;
  }

  function isSizeOptionLabel(label) {
    const normalized = String(label || "")
      .replace(/:\s*$/, "")
      .trim()
      .toLowerCase();
    return (
      normalized === "size" ||
      normalized === "us size" ||
      normalized === "uk size" ||
      normalized.indexOf("size") === 0
    );
  }

  function getVariantOptions(root) {
    const sectionId = root.getAttribute("data-section");
    if (sectionId) {
      const scoped = document.querySelector(
        `variant-options[data-section="${sectionId}"]`
      );
      if (scoped) return scoped;
    }
    return (
      document.querySelector("variant-options") ||
      document.querySelector("variant-selects")
    );
  }

  function getSizeValue(root) {
    const variantOptions = getVariantOptions(root);
    if (!variantOptions) {
      return root.getAttribute("data-initial-size") || "";
    }

    const optionRoots = variantOptions.querySelectorAll(".wt-product__option");
    for (const optionRoot of optionRoots) {
      const labelText =
        optionRoot.querySelector(".wt-product__option__title .label")
          ?.textContent || "";
      if (!isSizeOptionLabel(labelText)) continue;

      // Title value is updated by variants.js updateOptions()
      const titleValue = optionRoot
        .querySelector(".wt-product__option__title .value")
        ?.textContent?.trim();
      if (titleValue && parseSizeNumber(titleValue) != null) {
        return titleValue;
      }

      // Custom dropdown label (may be flattened to plain text by updateOptions)
      const dropdownValueEl = optionRoot.querySelector(
        ".js-custom-option-dropdown-value"
      );
      const dropdownLabel = optionRoot.querySelector(
        ".js-custom-option-dropdown-label"
      );
      const dropdownText = (
        dropdownValueEl?.textContent ||
        dropdownLabel?.textContent ||
        ""
      ).trim();
      if (dropdownText && parseSizeNumber(dropdownText) != null) {
        return dropdownText;
      }

      const checkedInput = optionRoot.querySelector(
        'input[type="radio"]:checked'
      );
      if (checkedInput?.value) return checkedInput.value;

      const selectedItem = optionRoot.querySelector(
        ".js-custom-option-item.is-selected"
      );
      if (selectedItem?.getAttribute("data-value")) {
        return selectedItem.getAttribute("data-value");
      }
    }

    // Last resort: first checked radio with a numeric size
    const checked = variantOptions.querySelector(
      'input[type="radio"]:checked'
    );
    if (checked?.value && parseSizeNumber(checked.value) != null) {
      return checked.value;
    }

    return root.getAttribute("data-initial-size") || "";
  }

  function plusImagesPromoted(root) {
    const sectionId = root.getAttribute("data-section");
    const gallery = sectionId
      ? document.getElementById(`MediaGallery-${sectionId}`)
      : document.querySelector("gallery-section");
    // Only after gallery.js has decided whether plus slides were moved first.
    if (gallery && gallery.dataset.plusPromoted != null) {
      return gallery.dataset.plusPromoted === "true";
    }
    return root.getAttribute("data-initial-plus") === "true";
  }

  function pickFitSet(root) {
    const sets = root.querySelectorAll("[data-fit-set]");
    if (!sets.length) return;

    const seed =
      root.getAttribute("data-product-handle") ||
      root.getAttribute("data-section") ||
      "";
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = (hash + seed.charCodeAt(i) * (i + 1)) % 997;
    }
    const pick = String(hash % 2);

    sets.forEach((set) => {
      set.hidden = set.getAttribute("data-fit-set") !== pick;
    });
    root.dataset.fitModel = pick;
  }

  function syncPanel(root) {
    pickFitSet(root);

    const standard = root.querySelector('[data-fit-panel="standard"]');
    const plus = root.querySelector('[data-fit-panel="plus"]');
    if (!standard || !plus) return;

    const sizeValue = getSizeValue(root);
    const size = parseSizeNumber(sizeValue);
    const showPlus =
      size != null && size >= PLUS_MIN && plusImagesPromoted(root);

    standard.hidden = showPlus;
    plus.hidden = !showPlus;
    root.dataset.currentSize = sizeValue || "";
    root.dataset.showPlus = showPlus ? "true" : "false";
  }

  function syncAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(syncPanel);
  }

  // Expose for variants.js updateOptions hook
  window.syncSizeAndFit = syncAll;

  function scheduleSync() {
    syncAll();
    requestAnimationFrame(syncAll);
    setTimeout(syncAll, 0);
    setTimeout(syncAll, 100);
  }

  function bind() {
    if (window.__sizeAndFitJsBound) return;
    window.__sizeAndFitJsBound = true;

    document.addEventListener(
      "change",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (
          target.closest("variant-options") ||
          target.closest("variant-selects") ||
          target.matches('input[type="radio"]')
        ) {
          scheduleSync();
        }
      },
      true
    );

    document.addEventListener(
      "click",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (
          target.closest(
            ".js-custom-option-item, .drawer__list__label, .wt-select__item, .wt-product__option__dropdown"
          )
        ) {
          scheduleSync();
        }
      },
      true
    );

    // variants.js dispatches on the element with bubbles:true
    document.addEventListener("variantChangeEnd", scheduleSync);
    document.addEventListener("variantChangeStart", scheduleSync);

    if (typeof subscribe === "function") {
      try {
        subscribe("variant-change", scheduleSync);
      } catch (e) {}
    }

    // Watch Size title value text mutations (variants.js writes here)
    const observer = new MutationObserver(scheduleSync);
    const observeTargets = () => {
      document
        .querySelectorAll(
          "variant-options .wt-product__option__title .value, .js-custom-option-dropdown-label, .js-custom-option-dropdown-value"
        )
        .forEach((el) => {
          observer.observe(el, {
            characterData: true,
            childList: true,
            subtree: true,
          });
        });
    };
    observeTargets();
    document.addEventListener("variantChangeEnd", observeTargets);
  }

  function init() {
    bind();
    scheduleSync();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Theme editor / section reloads
  document.addEventListener("shopify:section:load", init);
})();
